/**
 * AuthContext — single source of truth for the signed-in user.
 *
 * Owen Feb 28, 2026 (round 55) — diagnosis & fix:
 *   Each protected route was mounting its own copy of ProtectedRoute,
 *   which fired its own `/auth/me` request on every route change. When
 *   that one request hit the recurring 502/5xx hiccups (or just lagged),
 *   ProtectedRoute's 8-second timeout flipped to `<Navigate to="/" />`
 *   — Owen called this the "intermittent kick-out from Daily Journal".
 *
 * Fix: hold the auth state ONCE at the top of the app. Hydrate on boot
 * (and only on boot) from `/auth/me`, using the localStorage session
 * token as a "we should already be logged in" hint so reloads don't
 * flash a redirect before the check completes. Route transitions
 * inside the app are now synchronous — no extra network round trip,
 * no kick-out.
 *
 * Status states:
 *   - 'pending'        — initial check in flight (or about to run)
 *   - 'authenticated'  — confirmed signed-in
 *   - 'unauthenticated'— confirmed signed-out (401 from server, no token)
 *
 * Failure handling per Owen's spec:
 *   - 401 → unauthenticated, redirect-to-landing is OK
 *   - Network error / 5xx / 408 / 429 → up to 2 retries (2s apart). If
 *     all three attempts still transient, stay optimistically signed-in
 *     so the user keeps their session and isn't kicked out mid-flow.
 *     Individual API calls will then surface their own errors instead
 *     of the whole shell bouncing.
 */
import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = 'colosseum_session_token';

const AuthContext = createContext(null);

// Module-level hydration latch — survives the React.StrictMode double-mount
// that bricked the previous in-effect `cancelled`+`hydratedRef` pattern.
// Owen Feb 28, 2026 round 55 (post-test) — the testing agent caught the
// StrictMode race where cleanup set cancelled=true before /auth/me
// resolved, then the remount's `hydratedRef.current===true` bailed,
// leaving status='pending' forever. A module-scope boolean dodges the
// React reconciler entirely: the first effect ever to run owns the
// hydration; the second mount just no-ops.
let hydrationInFlight = false;

const readToken = () => {
  try {
    return typeof window !== 'undefined' ? window.localStorage.getItem(TOKEN_KEY) : null;
  } catch {
    return null;
  }
};

const writeToken = (value) => {
  try {
    if (value) window.localStorage.setItem(TOKEN_KEY, value);
    else window.localStorage.removeItem(TOKEN_KEY);
  } catch { /* localStorage may be disabled in private mode */ }
};

const isTransientAuthError = (error) => {
  if (!error) return false;
  const status = error.response?.status;
  if (status === 401) return false;       // real session loss
  if (!error.response) return true;       // network error / CORS / aborted
  if (status >= 500 && status < 600) return true;
  if (status === 408 || status === 429) return true;
  return false;
};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export const AuthProvider = ({ children }) => {
  // Boot status hint: if there's a stored token marker, we PROBABLY are
  // signed in — show the spinner instead of flashing the landing redirect
  // while /auth/me confirms. No token marker → start unauthenticated, no
  // round trip needed.
  const initialStatus = readToken() ? 'pending' : 'unauthenticated';
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState(initialStatus);

  useEffect(() => {
    if (status !== 'pending') return;
    // Module-level latch — only the very first mount across the whole
    // application kicks off /auth/me. StrictMode's second invocation
    // and any provider re-mount both fall through harmlessly.
    if (hydrationInFlight) return;
    hydrationInFlight = true;

    const hydrate = async () => {
      const MAX_RETRIES = 2;
      const RETRY_DELAY_MS = 2000;
      for (let attempt = 0; attempt <= MAX_RETRIES; attempt += 1) {
        try {
          const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
          setUser(res.data);
          setStatus('authenticated');
          // Sync i18n language from the backend so the app rehydrates in the
          // user's chosen language across devices. Backend wins over
          // localStorage because it persists across sign-ins on new devices.
          // Owen Jul 2026 — Italian-first push, Checkpoint 1.
          try {
            const serverLang = res.data?.preferred_language;
            if (serverLang && typeof serverLang === 'string') {
              const localLang = (typeof localStorage !== 'undefined')
                ? localStorage.getItem('i18nextLng') : null;
              if (serverLang !== localLang) {
                // Dynamic import to avoid a hard top-level dep on i18next
                // when the AuthContext is used outside the shell (SSR safe).
                const { default: i18n } = await import('../i18n');
                i18n.changeLanguage(serverLang);
                try { localStorage.setItem('i18nextLng', serverLang); } catch { /* ignore */ }
              }
            }
          } catch { /* language hydrate is best-effort */ }
          return;
        } catch (error) {
          if (!isTransientAuthError(error)) {
            // Real 401 / not-found → session is gone. Clear stale token,
            // route through the unauthenticated branch.
            writeToken(null);
            setUser(null);
            setStatus('unauthenticated');
            return;
          }
          if (attempt < MAX_RETRIES) {
            await sleep(RETRY_DELAY_MS);
          }
        }
      }
      // 3 transient failures in a row: KEEP THE USER OPTIMISTICALLY
      // SIGNED IN. Per Owen's spec — a network blip must NOT kick them
      // to the landing page. The next real API call will tell them the
      // truth (and the protected shell will keep rendering until then).
      setStatus('authenticated');
    };
    hydrate();
    // No cleanup function — module-level latch is the right mechanism
    // here; StrictMode unmount must NOT cancel an in-flight hydrate.
  }, [status]);

  const signIn = useCallback((newUser, newToken) => {
    if (newToken) writeToken(newToken);
    setUser(newUser || null);
    setStatus('authenticated');
  }, []);

  const signOut = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch { /* network failure — clear locally anyway */ }
    writeToken(null);
    setUser(null);
    setStatus('unauthenticated');
  }, []);

  // Lightweight refetch — useful after an action that mutates the user
  // doc (e.g. theme save, age-bracket update). Optional; not required
  // for ProtectedRoute correctness.
  const refresh = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(res.data);
      setStatus('authenticated');
      return res.data;
    } catch (error) {
      if (error?.response?.status === 401) {
        writeToken(null);
        setUser(null);
        setStatus('unauthenticated');
      }
      return null;
    }
  }, []);

  const value = {
    user,
    setUser,
    status,
    isAuthenticated: status === 'authenticated',
    loading: status === 'pending',
    signIn,
    signOut,
    refresh,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    // Defensive — should never happen in practice (App.js wraps the tree).
    // Returning a safe stub keeps non-protected pages from crashing if a
    // component is accidentally rendered outside the provider in tests.
    return {
      user: null,
      setUser: () => {},
      status: 'unauthenticated',
      isAuthenticated: false,
      loading: false,
      signIn: () => {},
      signOut: async () => {},
      refresh: async () => null,
    };
  }
  return ctx;
};

export default AuthContext;
